'use client'

import { useRef } from 'react'
import { motion } from 'motion/react'
import { SearchIcon, XIcon } from 'design-configuration/components'
import { BodyText, DetailText } from 'design-configuration/components'

export interface OmnisearchProps {
  value: string
  onChange: (value: string) => void
  savedValues: string[]
  onSave: () => void
  onRemove: (value: string) => void
  placeholder?: string
  className?: string
  label?: string
}

export function Omnisearch({
  value,
  onChange,
  savedValues,
  onSave,
  onRemove,
  placeholder = 'Enter search terms...',
  className = '',
  label
}: OmnisearchProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSave()
    } else if (e.key === 'Backspace') {
      if (value === '' && savedValues.length > 0) {
        onRemove(savedValues[savedValues.length - 1])
      }
    }
  }

  return (
    <div className={`gap-y-sm flex flex-col ${className}`}>
      {label && <DetailText className="text-on-surface-variant font-medium">{label}</DetailText>}
      <motion.button
        layout="position"
        className="border-outline-variant p-xs gap-2xs rounded-xs flex h-auto w-full flex-wrap items-center border"
        onClick={() => inputRef.current?.focus()}
      >
        <SearchIcon className="w-sm h-sm fill-on-surface-variant shrink-0" />

        {savedValues.map((savedValue) => (
          <motion.button
            layout="position"
            key={savedValue}
            className="px-xs border-outline-variant gap-x-xs flex items-center rounded-full border"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(savedValue)
            }}
          >
            <DetailText>{savedValue}</DetailText>
            <XIcon className="w-xs h-xs fill-on-surface-variant" />
          </motion.button>
        ))}

        <BodyText
          className={`${savedValues.length ? `w-[120px]` : 'w-4/5'} grow-0 overflow-hidden text-wrap text-left`}
        >
          <input
            placeholder={savedValues.length ? '' : placeholder}
            type="text"
            className="w-full bg-transparent focus-visible:outline-none"
            ref={inputRef}
            onChange={(e) => onChange(e.target.value)}
            value={value}
            onKeyUp={handleKeyUp}
          />
        </BodyText>
      </motion.button>
    </div>
  )
}

// Export shared search utilities
export { useSearchTerms, type SearchTermsState, type SearchTermsConfig } from './use-search-terms'
